
import { defineComponent } from "vue";
import ApiService from "@/core/services/ApiService";
import XypValueRenderer from "@/components/xyp/XypValueRenderer.vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import {
  xypRole,
  xypServices,
  xypTitle,
  XypServiceConfig
} from "@/core/config/XypServiceConfig";
import { ElNotification } from "element-plus";

export default defineComponent({
  name: "XypService",
  components: { XypValueRenderer },
  data() {
    return {
      services: xypServices,
      form: {} as Record<string, string>,
      result: null as any,
      elapsedMs: 0,
      loading: false,
      serviceSearch: ""
    };
  },
  computed: {
    service(): XypServiceConfig {
      const code =
        (this.$route.params.serviceCode as string) || xypServices[0].code;
      return (
        this.services.find(item => item.code === code) ||
        this.services[0] ||
        xypServices[0]
      );
    },
    title(): string {
      return xypTitle(this.service);
    },
    canAccess(): boolean {
      return this.$isMbUser && this.$hasRole(xypRole(this.service.code));
    },
    filteredServices(): XypServiceConfig[] {
      const search = this.serviceSearch.toLowerCase().trim();
      return this.services.filter(item => {
        const allowed = this.$hasRole(xypRole(item.code));
        const matched =
          !search || `${item.name} ${item.code}`.toLowerCase().includes(search);
        return allowed && matched;
      });
    },
    groupedServices(): Array<{ category: string; items: XypServiceConfig[] }> {
      const groups = new Map<string, XypServiceConfig[]>();
      this.filteredServices.forEach(item => {
        const items = groups.get(item.category) || [];
        items.push(item);
        groups.set(item.category, items);
      });
      return Array.from(groups.entries()).map(([category, items]) => ({
        category,
        items
      }));
    },
    responseMeta(): any {
      let payload = this.parseJsonValue(this.result);
      for (let i = 0; i < 5; i++) {
        const next = payload?.response;
        if (!next) {
          break;
        }
        payload = this.parseJsonValue(next);
      }
      return payload || {};
    },
    resultData(): any {
      const data = this.parseJsonValue(this.responseMeta?.data);
      if (data !== undefined && data !== null) {
        return data;
      }
      const rest = { ...(this.responseMeta || {}) };
      delete rest.result;
      delete rest.resultcode;
      delete rest.service;
      return Object.keys(rest).length ? rest : this.responseMeta;
    },
    imageValue(): string {
      return this.resultData?.imageAsBase64 || "";
    },
    prettyResponse(): string {
      return JSON.stringify(this.responseMeta, null, 2);
    }
  },
  watch: {
    "$route.params.serviceCode": {
      handler() {
        this.resetState();
        setCurrentPageTitle(this.title);
      }
    }
  },
  async mounted() {
    setCurrentPageTitle(this.title);
    this.resetState();
    await this.loadServices();
  },
  methods: {
    xypRole,
    async loadServices() {
      try {
        const response = await ApiService.get("xyp/services", "");
        const remote = response.data?.response;
        if (Array.isArray(remote) && remote.length) {
          this.services = remote;
        }
      } catch (e) {
        // The bundled catalog keeps the page usable if metadata loading fails.
      }
    },
    resetState() {
      this.form = {};
      this.result = null;
      this.elapsedMs = 0;
      this.service.inputs.forEach(input => {
        this.form[input.name] = "";
      });
    },
    openService(code: string) {
      this.$router.push({ name: "xyp-service", params: { serviceCode: code } });
    },
    clearForm() {
      this.resetState();
    },
    parseJsonValue(value: any): any {
      if (typeof value !== "string") {
        return value;
      }
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    },
    validateForm(): boolean {
      const required = this.service.inputs.filter(input => input.required);
      if (required.length) {
        return required.every(input => !!this.form[input.name]?.trim());
      }
      return this.service.inputs.some(input => !!this.form[input.name]?.trim());
    },
    async submit() {
      if (!this.canAccess) {
        return;
      }
      if (!this.validateForm()) {
        ElNotification({
          title: "Анхааруулга",
          message: "Хайх утгаа оруулна уу.",
          type: "warning"
        });
        return;
      }
      this.result = null;
      this.elapsedMs = 0;
      this.loading = true;
      try {
        const response = await ApiService.postWithRawBody(
          `xyp/services/${this.service.code}`,
          { params: this.form }
        );
        if (response.data.code !== 1) {
          ElNotification({
            title: response.data.code === 401 ? "Анхааруулга" : "Алдаа",
            message:
              response.data.warning ||
              response.data.error ||
              "Сервис дуудах үед алдаа гарлаа.",
            type: response.data.code === 401 ? "warning" : "error"
          });
          return;
        }
        this.result = response.data.response;
        this.elapsedMs = response.data.elapsedMs || 0;
      } catch (e) {
        ElNotification({
          title: "Алдаа",
          message: "ХУР сервис дуудах үед алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.loading = false;
      }
    }
  }
});
